import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Flip from "gsap/Flip";
import { Guides } from "./components/guides.js";
import { ImageZoom } from "./components/imageZoom.js";
import { OldBrowserChecker } from "./components/oldBrowserChecker.js";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, Flip);

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

const isReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const isDesktop = () => {
  return window.matchMedia("(min-width: 1000px)").matches;
};

let mm = gsap.matchMedia();

// Gets CSS variable value and converts it to pixels
const getVariable = (variable) => {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
  const value = getComputedStyle(document.documentElement).getPropertyValue(
    variable
  );

  if (value.includes("rem")) {
    return parseFloat(value) * rem;
  }

  return parseFloat(value);
};

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

new OldBrowserChecker();

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

new Guides();

/* -------------------------------------------------- */
/* 🍱 Menu */
/* -------------------------------------------------- */

const menu = document.querySelector(".menu");
const menuToggle = document.querySelector(".menu-toggle");
const menuItems = document.querySelectorAll(".menu__item");

menuToggle.addEventListener("click", () => {
  const isExpanded = menuToggle.getAttribute("aria-expanded") === "true";
  menuToggle.setAttribute("aria-expanded", isExpanded ? "false" : "true");

  if (isExpanded) {
    gsap.to(menu, {
      opacity: 0,
      duration: 0.25,
      ease: "power2.out",
      onComplete: () => {
        menu.style.visibility = "hidden";
        menu.style.opacity = null;
      },
    });
  } else {
    gsap
      .timeline()

      .to(menu, {
        autoAlpha: 1,
        duration: 0.25,
        ease: "power2.out",
      })

      .fromTo(
        menuItems,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.75,
          stagger: 0.05,
        }
      );
  }
});

/* -------------------------------------------------- */
/* 🔗 Work links */
/* -------------------------------------------------- */

const workLinks = document.querySelectorAll(".work-link");

workLinks.forEach((workLink) => {
  const title = workLink.querySelector(".work-link__title");

  gsap
    .timeline({
      defaults: {
        duration: 1,
        ease: "power2.inOut",
      },
      scrollTrigger: {
        trigger: title,
        start: "top 90%",
        end: "bottom 10%",
        scrub: 0.5,
        invalidateOnRefresh: true,
      },
    })
    .to(title, {
      opacity: 1,
    })
    .to(title, {})
    .to(title, {
      opacity: 0,
    });
});

/* -------------------------------------------------- */
/* 🌅 Work images */
/* -------------------------------------------------- */

const workImagesWrap = document.querySelector(".work-images");

if (workImagesWrap) {
  const workImagesContainer = document.querySelector(".work-images__container");
  const workImages = workImagesContainer.querySelectorAll(".work-images__item");

  // Desktop-only pins
  mm.add("(min-width: 1001px)", () => {
    // Pin with horizontal scroll
    const horizontalScroll = gsap.to(workImagesContainer, {
      x: () => -(workImagesContainer.offsetWidth - workImagesWrap.offsetWidth),
      ease: "none",
      scrollTrigger: {
        pin: workImagesWrap,
        scrub: 0.5,
        invalidateOnRefresh: true,
        start: 0,
        end: () =>
          "+=" + (workImagesContainer.offsetWidth - workImagesWrap.offsetWidth),
      },
    });

    const scrollTrigger = horizontalScroll.scrollTrigger;

    // Handle keyboard navigation
    workImages.forEach((image) => {
      image.addEventListener("focusin", () => {
        if (image.matches(":focus-visible")) {
          scrollTrigger.scroll(image.offsetLeft);
        }
      });
    });

    // // Pin after scrolled to top
    // ScrollTrigger.create({
    //   trigger: workImagesWrap,
    //   start: "top " + getVariable("--height-header"),
    //   end: "max",
    //   pin: workImagesWrap,
    // });
  });

  // Click to zoom
  if (isDesktop()) {
    new ImageZoom(document.querySelectorAll(".work-images__item"));
  }
}

/* -------------------------------------------------- */
/* 🕴️ Vita */
/* -------------------------------------------------- */

// Pin with horizontal scroll

const vitaBlock = document.querySelector(".vita-block");

if (vitaBlock) {
  const horizontalScroller = vitaBlock.querySelector(".horizontal-scroller");

  gsap.to(horizontalScroller, {
    x: () => {
      return -(horizontalScroller.scrollWidth - horizontalScroller.offsetWidth);
    },
    ease: "none",
    scrollTrigger: {
      pin: "main",
      trigger: horizontalScroller,
      scrub: 0.5,
      invalidateOnRefresh: true,
      start: "center center",
      end: () =>
        "+=" +
        (horizontalScroller.scrollWidth - horizontalScroller.offsetWidth),
    },
  });
}

/* -------------------------------------------------- */
/* 🗂️ Index */
/* -------------------------------------------------- */

const index = document.querySelector(".index");

if (index) {
  const indexItems = document.querySelectorAll(".index__item");
  const pageTitle = document.querySelector(".page-title");

  new ImageZoom(indexItems);

  indexItems.forEach((indexItem) => {
    const title = indexItem.dataset.title;

    indexItem.addEventListener("pointerenter", () => {
      pageTitle.textContent = title;

      gsap.to(pageTitle, {
        opacity: 1,
        duration: 0.5,
        ease: "power1.inOut",
      });
    });

    indexItem.addEventListener("pointerleave", () => {
      gsap.to(pageTitle, {
        opacity: 0,
        duration: 0.5,
        ease: "power1.inOut",
      });
    });
  });
}

/* -------------------------------------------------- */
/* ↩️ Intelligent back link */
/* -------------------------------------------------- */

let backLinks = document.querySelectorAll("a.back-link");

backLinks.forEach((backLink) => {
  backLink.addEventListener("click", function (e) {
    const previousPage = document.referrer;
    const currentDomain = window.location.hostname;

    if (previousPage && previousPage.includes(currentDomain)) {
      e.preventDefault();
      window.history.back();
    }
  });
});

/* -------------------------------------------------- */
/* 🦥 Lazy loading */
/* -------------------------------------------------- */

const lazyLoadedElements = document.querySelectorAll('[loading="lazy"]');

lazyLoadedElements.forEach((element) => {
  const fadeIn = gsap.to(element, {
    opacity: 1,
    duration: 1,
    ease: "power2.inOut",
    paused: true,
  });

  if (element.complete) {
    fadeIn.play();
  } else {
    element.onload = () => {
      fadeIn.play();
    };
  }
});
